import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AWS_CONFIG } from '../config/aws';
import { SERVICES as FALLBACK_SERVICES, VEHICLE_TYPES } from '../data/services';
import { FILTER_TABS, getToday, getWeekRange } from './bookingConstants';
import BookingsTable from './BookingsTable';
import ConfirmDialog from './ConfirmDialog';

export default function BookingManager() {
  const { getIdToken } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [services, setServices] = useState(FALLBACK_SERVICES);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [updating, setUpdating] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [fetchError, setFetchError] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [actionError, setActionError] = useState('');

  useEffect(() => {
    fetch(`${AWS_CONFIG.apiUrl}/services`)
      .then(r => r.json())
      .then(data => { if (data.services?.length > 0) setServices(data.services); })
      .catch(() => {});
  }, []);

  // Support both old format (string ID) and new format (snapshot object)
  const renderService = (entry) => {
    if (entry && typeof entry === 'object') {
      // Snapshot format: { serviceId, name, price } — preserves historical name
      const svcId = entry.serviceId;
      const stillExists = services.some(x => (x.serviceId || x.id) === svcId);
      return stillExists
        ? (entry.name || svcId)
        : `${entry.name || svcId} (삭제됨)`;
    }
    // Legacy format: raw service ID — look up current
    const s = services.find(x => (x.serviceId || x.id) === entry);
    return s?.name || `${entry} (삭제됨)`;
  };

  const vehicleName = (id) => {
    const v = VEHICLE_TYPES.find(x => x.id === id);
    return v?.name || id;
  };

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const token = await getIdToken();
      if (!token) {
        setFetchError({ type: 'no-token', message: '로그인 세션이 만료되었습니다. 다시 로그인해주세요.' });
        setLoading(false);
        return;
      }
      let url = `${AWS_CONFIG.apiUrl}/bookings`;
      const params = [];
      if (dateFilter === 'today') {
        params.push(`date=${getToday()}`);
      }
      if (statusFilter !== 'all') {
        params.push(`status=${statusFilter}`);
      }
      if (params.length > 0) url += '?' + params.join('&');

      const res = await fetch(url, {
        headers: { Authorization: token },
      });
      if (!res.ok) {
        let body = '';
        try { body = JSON.stringify(await res.json()); } catch { body = await res.text(); }
        setFetchError({ type: 'http', status: res.status, body });
        setLoading(false);
        return;
      }
      const data = await res.json();
      let items = data.bookings || [];

      // Client-side filtering for "this week" (API only supports single date)
      if (dateFilter === 'week') {
        const { start, end } = getWeekRange();
        items = items.filter(b => b.date >= start && b.date <= end);
      }

      setBookings(items);
    } catch (err) {
      console.error('Failed to fetch bookings:', err);
      setFetchError({ type: 'network', message: err.message || String(err) });
    }
    setLoading(false);
  }, [getIdToken, dateFilter, statusFilter]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  const performDelete = async (b) => {
    setConfirmTarget(null);
    setDeleting(b.bookingId);
    setActionError('');
    try {
      const token = await getIdToken();
      const res = await fetch(`${AWS_CONFIG.apiUrl}/bookings/${b.bookingId}`, {
        method: 'DELETE',
        headers: { Authorization: token },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setActionError(`삭제 실패: ${body.error || res.status}`);
      } else {
        await fetchBookings();
      }
    } catch (err) {
      setActionError(`서버 오류: ${err.message}`);
    }
    setDeleting(null);
  };

  const handleDelete = (b) => {
    setConfirmTarget(b);
  };

  const handleStatusChange = async (bookingId, newStatus) => {
    setUpdating(bookingId);
    setActionError('');
    try {
      const token = await getIdToken();
      await fetch(`${AWS_CONFIG.apiUrl}/bookings/${bookingId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify({ status: newStatus }),
      });
      await fetchBookings();
    } catch (err) {
      console.error('Failed to update status:', err);
      setActionError('상태 변경에 실패했습니다.');
    }
    setUpdating(null);
  };

  const tabStyle = (active) => ({
    padding: '8px 16px', borderRadius: 'var(--radius)',
    border: '1px solid', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600,
    borderColor: active ? 'var(--primary)' : 'var(--border)',
    background: active ? 'var(--primary)' : 'var(--surface)',
    color: active ? '#fff' : 'var(--text)',
  });

  return (
    <div>
      <h1>예약 관리</h1>

      {actionError && (
        <div style={{
          padding: '10px 14px', marginBottom: 16, borderRadius: 'var(--radius)',
          background: 'rgba(255, 82, 82, 0.1)', border: '1px solid #ff5252',
          color: '#ff5252', fontSize: '0.9rem',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span>{actionError}</span>
          <button
            type="button"
            onClick={() => setActionError('')}
            style={{
              background: 'transparent', border: 'none',
              color: '#ff5252', cursor: 'pointer', fontSize: '1.1rem',
            }}
            aria-label="닫기"
          >×</button>
        </div>
      )}

      <ConfirmDialog
        open={!!confirmTarget}
        title="예약 완전 삭제"
        message={confirmTarget
          ? `"${confirmTarget.customerName}"님의 ${confirmTarget.date} ${confirmTarget.time} 예약을 완전히 삭제할까요?\n삭제된 예약은 복구할 수 없습니다.`
          : ''}
        confirmLabel="삭제"
        cancelLabel="취소"
        danger
        onConfirm={() => confirmTarget && performDelete(confirmTarget)}
        onCancel={() => setConfirmTarget(null)}
      />

      {/* Date filter */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {[
          { key: 'today', label: '오늘' },
          { key: 'week', label: '이번 주' },
          { key: 'all', label: '전체' },
        ].map(f => (
          <button key={f.key} style={tabStyle(dateFilter === f.key)}
            onClick={() => setDateFilter(f.key)}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Status filter */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        {FILTER_TABS.map(f => (
          <button key={f.key} style={tabStyle(statusFilter === f.key)}
            onClick={() => setStatusFilter(f.key)}>
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p style={{ color: 'var(--text-muted)' }}>로딩 중...</p>
      ) : fetchError ? (
        <div className="card" style={{ padding: 20, borderColor: '#ff5252', background: 'rgba(255, 82, 82, 0.08)' }}>
          <p style={{ color: '#ff5252', fontWeight: 700, marginBottom: 8 }}>❌ 예약을 불러오지 못했습니다</p>
          {fetchError.type === 'http' && (
            <>
              <p style={{ fontSize: '0.9rem', marginBottom: 4 }}>HTTP 상태: <strong>{fetchError.status}</strong></p>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', wordBreak: 'break-all' }}>
                응답: {fetchError.body}
              </p>
            </>
          )}
          {fetchError.type === 'network' && (
            <p style={{ fontSize: '0.9rem' }}>네트워크/CORS 오류: {fetchError.message}</p>
          )}
          {fetchError.type === 'no-token' && (
            <p style={{ fontSize: '0.9rem' }}>{fetchError.message}</p>
          )}
          <button className="btn btn-outline" onClick={fetchBookings}
            style={{ marginTop: 12, fontSize: '0.85rem', padding: '6px 14px' }}>
            다시 시도
          </button>
        </div>
      ) : (
        <BookingsTable
          bookings={bookings}
          services={services}
          vehicleName={vehicleName}
          renderService={renderService}
          updating={updating}
          deleting={deleting}
          onStatusChange={handleStatusChange}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
}
